import React, { useEffect, useMemo, useState } from 'react'
import homeHeroImg from '../mobile/home.jpg'
import suggectionsImg from '../mobile/suggections.jpg'
import screenImg from '../mobile/screen.jpg'
import practiceImg from '../mobile/practice.jpg'
import TipPage from './TipPage'

function getRuntimeConfig() {
  const cfg = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {}
  const appName = cfg.APP_NAME || import.meta.env.VITE_APP_NAME || 'EmaTalking'
  const apkVersion = cfg.APK_VERSION || import.meta.env.VITE_APK_VERSION || ''
  const rawAndroidApkUrl =
    cfg.ANDROID_APK_URL ||
    import.meta.env.VITE_ANDROID_APK_URL ||
    'https://ematalking.cn/app-release.apk'
  const androidApkUrl = apkVersion ? `${rawAndroidApkUrl}?v=${encodeURIComponent(apkVersion)}` : rawAndroidApkUrl
  const androidApkFileName =
    cfg.ANDROID_APK_FILE_NAME ||
    import.meta.env.VITE_ANDROID_APK_FILE_NAME ||
    `${appName}.apk`

  return {
    appName,
    apkVersion,
    androidApkUrl,
    androidApkFileName,
    iosUrl: cfg.IOS_URL || import.meta.env.VITE_IOS_URL || ''
  }
}

function detectEnv() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const isAndroid = /Android/i.test(ua)
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
  const isWeChat = /MicroMessenger/i.test(ua)
  const isQQ = /\bQQ\//i.test(ua)
  const isWeibo = /Weibo/i.test(ua)
  return { ua, isAndroid, isIOS, isWeChat, isQQ, isWeibo }
}

function Button({ href, download, target, rel, onClick, variant = 'solid', disabled, children, className = '' }) {
  const cn = `btn btn--${variant}${disabled ? ' is-disabled' : ''} ${className}`.trim()
  if (href) {
    return (
      <a
        className={cn}
        href={disabled ? undefined : href}
        download={disabled ? undefined : download}
        target={target}
        rel={rel}
        onClick={onClick}
        role="button"
        aria-disabled={disabled ? 'true' : 'false'}
      >
        {children}
      </a>
    )
  }
  return (
    <button className={cn} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  )
}

function Icon({ name }) {
  if (name === 'apple') {
    return (
      <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M16.7 13.2c0-1.9 1.6-2.8 1.7-2.9-1-1.5-2.5-1.7-3-1.8-1.3-.1-2.5.8-3.1.8-.6 0-1.6-.8-2.7-.8-1.4 0-2.7.8-3.4 2.1-1.5 2.5-.4 6.2 1 8.3.7 1 1.5 2.1 2.6 2 1-.1 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1 0 1.9-1 2.5-2 .8-1.1 1.1-2.2 1.1-2.2-.1 0-2.1-.8-2.1-3.5ZM14.9 6.9c.5-.6.9-1.5.8-2.4-.8.1-1.7.5-2.3 1.1-.5.6-.9 1.4-.8 2.3.9.1 1.8-.4 2.3-1Z" />
      </svg>
    )
  }
  if (name === 'google') {
    return (
      <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 11.2v2.9h4.1c-.2 1-1.2 2.9-4.1 2.9a4.7 4.7 0 0 1 0-9.4c1.6 0 2.7.7 3.3 1.3l2.2-2.1C16.1 5.4 14.3 4.6 12 4.6a7.4 7.4 0 1 0 0 14.8c4.3 0 7.1-3 7.1-7.2 0-.5-.1-.9-.1-1.3H12Z" />
      </svg>
    )
  }
  if (name === 'menu') {
    return (
      <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 6.5h16v2H4v-2Zm0 7h16v2H4v-2Z" />
      </svg>
    )
  }
  if (name === 'close') {
    return (
      <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M18.3 5.7 12 12l6.3 6.3-1.4 1.4L10.6 13.4 4.3 19.7 2.9 18.3 9.2 12 2.9 5.7 4.3 4.3l6.3 6.3 6.3-6.3 1.4 1.4Z" />
      </svg>
    )
  }
  if (name === 'check') {
    return (
      <svg className="icon" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9.2 16.6 4.9 12.3l1.4-1.4 2.9 2.9 8.5-8.5 1.4 1.4-9.9 9.9Z" />
      </svg>
    )
  }
  return null
}

export default function App() {
  const cfg = useMemo(() => getRuntimeConfig(), [])
  const env = useMemo(() => detectEnv(), [])
  const [menuOpen, setMenuOpen] = useState(false)
  const [iosNotSupportedOpen, setIosNotSupportedOpen] = useState(false)
  const [showTipPage, setShowTipPage] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') return
    const prev = document.body.style.overflow
    const lockScroll = menuOpen || iosNotSupportedOpen
    document.body.style.overflow = lockScroll ? 'hidden' : prev || ''
    return () => {
      document.body.style.overflow = prev || ''
    }
  }, [menuOpen, iosNotSupportedOpen])

  useEffect(() => {
    if (!iosNotSupportedOpen || typeof window === 'undefined') return
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIosNotSupportedOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [iosNotSupportedOpen])

  const primaryCta = useMemo(() => {
    if (env.isIOS) return { label: '前往 iOS 下载', href: cfg.iosUrl, platform: 'ios' }
    if (env.isAndroid) return { label: '下载 Android 安装包', href: cfg.androidApkUrl, platform: 'android' }
    return { label: '下载 App', href: cfg.androidApkUrl || cfg.iosUrl, platform: 'generic' }
  }, [env.isIOS, env.isAndroid, cfg.androidApkUrl, cfg.iosUrl])

  const showInAppBrowserHint = env.isWeChat || env.isQQ || env.isWeibo
  const androidDisabled = !cfg.androidApkUrl
  const iosDisabled = !cfg.iosUrl

  if (showTipPage) {
    return <TipPage appName={cfg.appName} onBack={() => setShowTipPage(false)} />
  }

  return (
    <div className="page">
      {showInAppBrowserHint ? (
        <div className="banner" role="note">
          当前处于内置浏览器，可能无法直接安装。请点击右上角菜单，选择“在浏览器中打开”后再下载。
        </div>
      ) : null}

      {iosNotSupportedOpen ? (
        <div className="modal" role="dialog" aria-modal="true" aria-label="提示">
          <div className="modal__mask" onClick={() => setIosNotSupportedOpen(false)} />
          <div className="modal__panel">
            <div className="modal__title">提示</div>
            <div className="modal__content">暂不支持 iOS 版本</div>
            <div className="modal__actions">
              <button className="btn btn--solid modal__btn" onClick={() => setIosNotSupportedOpen(false)}>
                我知道了
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <header className="topbar">
        <div className="container topbar__inner">
          <div className="brand">
            <div className="brand__logo" aria-hidden="true">
              <span className="brand__logoLetter">E</span>
            </div>
            <div className="brand__name">{cfg.appName}</div>
          </div>
          <div className="topbar__actions">
            <Button href="#download" variant="solid" className="topbar__download">
              下载APP
            </Button>
            <button className="iconBtn" onClick={() => setMenuOpen(true)} aria-label="打开菜单">
              <Icon name="menu" />
            </button>
          </div>
        </div>
      </header>

      {menuOpen ? (
        <div className="drawer" role="dialog" aria-modal="true">
          <div className="drawer__mask" onClick={() => setMenuOpen(false)} />
          <div className="drawer__panel">
            <div className="drawer__head">
              <div className="drawer__title">菜单</div>
              <button className="iconBtn" onClick={() => setMenuOpen(false)} aria-label="关闭菜单">
                <Icon name="close" />
              </button>
            </div>
            <div className="drawer__links">
              <a href="#features" onClick={() => setMenuOpen(false)}>
                为什么选择 {cfg.appName}
              </a>  
              <a href="#progress" onClick={() => setMenuOpen(false)}>
                成长与复盘
              </a>
              <a href="#download" onClick={() => setMenuOpen(false)}>
                下载
              </a>
              <a href="#footer" onClick={() => setMenuOpen(false)}>
                隐私与条款
              </a>
              <button
                type="button"
                className="drawer__tipLink"
                onClick={() => {
                  setMenuOpen(false)
                  setShowTipPage(true)
                }}
              >
                ☕ 打赏作者
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <main>
        <section className="hero">
          <div className="container hero__inner">
            <div className="hero__copy">
              <h1 className="hero__title">
                你的 <span className="hl">24小时</span>
                <br />
                AI 英语口语私教
              </h1>
              <p className="hero__desc">
                {cfg.appName} 是一款 <b>完全免费的英语口语对练APP</b>：随时随地开口练口语，AI 角色扮演对话与情景模拟，实时语法纠错与更地道的表达建议。
              </p>
              <div className="badges" aria-label="产品亮点">
                <span className="badge">100% 免费</span>
                <span className="badge">口语对练</span>
                <span className="badge">实时纠错</span>
              </div>

              <div className="storeRow">
                <Button
                  href={cfg.iosUrl || '#!'}
                  disabled={iosDisabled}
                  onClick={(e) => {
                    e?.preventDefault?.()
                    setIosNotSupportedOpen(true)
                  }}
                  variant="store store--apple"
                >
                  <Icon name="apple" />
                  <span className="storeText storeText--single">iOS 立即下载</span>
                </Button>
                <Button href={cfg.androidApkUrl} download={cfg.androidApkFileName} disabled={androidDisabled} variant="store store--google">
                  <Icon name="google" />
                  <span className="storeText storeText--single">Android APK 安全下载</span>
                </Button>
              </div>
            </div>

            <div className="hero__device" aria-hidden="true">
              <img className="hero__deviceImg" src={homeHeroImg} alt="" loading="lazy" />
            </div>
          </div>
        </section>

        <section id="features" className="section">
          <div className="container">
            <h2 className="section__title">为什么选择 {cfg.appName}?</h2>

            <div className="featureList">
              <div className="featureCard">
                <div className="featureArt featureArt--grammar" aria-hidden="true">
                  <img className="hero__deviceImg featureArtPhoneImg" src={suggectionsImg} alt="" loading="lazy" />
                </div>
                <div className="featureTitle">实时语法纠错</div>
                <div className="featureDesc">AI 实时捕捉语法漏洞并提供地道表达建议。</div>
              </div>

              <div className="featureCard">
                <div className="featureArt featureArt--scene" aria-hidden="true">
                  <img className="hero__deviceImg featureArtPhoneImg" src={screenImg} alt="" loading="lazy" />
                </div>
                <div className="featureTitle">海量实景模拟</div>
                <div className="featureDesc">覆盖职场、旅游、生活等 100+ 真实场景。</div>
              </div>

              <div className="featureCard">
                <div className="featureArt featureArt--voice" aria-hidden="true">
                  <img className="hero__deviceImg featureArtPhoneImg" src={practiceImg} alt="" loading="lazy" />
                </div>
                <div className="featureTitle">雅思真题免费练习</div>
                <div className="featureDesc">海量雅思真题资源，免费练习，助你轻松备考雅思口语。</div>
              </div>
            </div>
          </div>
        </section>

        <section id="progress" className="section section--alt">
          <div className="container progress">
            <div className="progress__copy">
              <h2 className="section__title">看得见的进步，<br />每一天都在成长</h2>
              <ul className="checkList">
                <li>
                  <Icon name="check" />
                  自动生成每日学习报告
                </li>
                <li>
                  <Icon name="check" />
                  AI 智能分析薄弱环节
                </li>
                <li>
                  <Icon name="check" />
                  定制化专属提升计划
                </li>
              </ul>
            </div>
            <div className="progress__mock" aria-hidden="true">
              <div className="mockCard">
                <div className="mockHead">Learning Progress</div>
                <div className="bars">
                  <span style={{ height: '45%' }} />
                  <span style={{ height: '62%' }} />
                  <span style={{ height: '54%' }} />
                  <span style={{ height: '72%' }} />
                  <span style={{ height: '68%' }} />
                  <span style={{ height: '80%' }} />
                  <span style={{ height: '76%' }} />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="download" className="ctaSection">
          <div className="container ctaSection__inner">
            <div className="ctaTitle">
              准备好
              <br />
              流利说英语了吗？
            </div>
            <div className="ctaButtons">
              <Button href={cfg.iosUrl} disabled={iosDisabled} variant="cta cta--ios">
                <Icon name="apple" /> iOS下载
              </Button>
              <Button href={cfg.androidApkUrl} download={cfg.androidApkFileName} disabled={androidDisabled} variant="cta cta--android">
                Android下载
              </Button>
            </div>
          </div>
        </section>

        <section id="faq" className="section">
          <div className="container">
            <h2 className="section__title">常见问题</h2>
            <div className="faqList">
              <details className="faqItem" open>
                <summary>这款 APP 真的完全免费吗？</summary>
                <div className="faqAnswer">
                  是的，{cfg.appName} 当前所有功能均可免费使用，暂无订阅/内购门槛。我们会在官网与 App 内同步更新政策变更。
                </div>
              </details>
              <details className="faqItem">
                <summary>适合哪些人群？</summary>
                <div className="faqAnswer">
                  适合想提升英语口语流利度与表达地道度的人群：日常对话、职场沟通、旅游交流，以及雅思/托福口语备考等。
                </div>
              </details>
              <details className="faqItem">
                <summary>如何下载与安装？</summary>
                <div className="faqAnswer">
                  Android 可直接下载 APK 安装；如在微信/QQ/微博内打开，请先“在浏览器中打开”再安装。iOS 入口如显示灰色，代表当前未开放下载。
                </div>
              </details>
            </div>
          </div>
        </section>

        <footer id="footer" className="footer">
          <div className="container footer__inner">
            <div className="footer__links">
              <a href="/privacy.html">隐私政策</a>
              <span className="footer__sep">|</span>
              <a href="/terms.html">用户协议</a>
            </div>
            <button
              type="button"
              className="footer__tipBtn"
              onClick={() => setShowTipPage(true)}
            >
              <span aria-hidden="true">☕</span>
              打赏作者
            </button>
            <div className="footer__copy">© {new Date().getFullYear()} {cfg.appName}. All Rights Reserved.</div>
          </div>
        </footer>
      </main>
    </div>
  )
}

